/** Driver app — Pogodnosti za vozače: partner discounts catalog. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Gift, BadgePercent, X, Globe, Phone } from "lucide-react";
import { toast } from "sonner";
import driverApi from "@/lib/driverApi";

export default function DriverBenefits() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    driverApi.get("/driver/benefits")
      .then(({ data }) => setRows(data))
      .catch(() => toast.error("Učitavanje pogodnosti nije uspjelo."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100 pb-16">
      <div className="max-w-xl mx-auto px-4 pt-6" data-testid="driver-benefits-page">
        <Link to="/driver/dashboard" className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-white transition mb-4">
          <ArrowLeft className="w-4 h-4" /> Natrag na početnu
        </Link>
        <div className="flex items-center gap-2 mb-1">
          <Gift className="w-5 h-5 text-[#C5A059]" />
          <h1 className="text-xl font-light text-white">Pogodnosti za vozače</h1>
        </div>
        <p className="text-xs text-zinc-500 mb-5">Ekskluzivni popusti kod partnera Juke Transfersa — dijelovi, servisi i više.</p>

        {loading ? (
          <p className="text-sm text-zinc-500">Učitavanje…</p>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center" data-testid="benefits-empty">
            <p className="text-zinc-400 text-sm">Pogodnosti uskoro stižu — provjerite ponovno kasnije.</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {rows.map((b) => (
              <button key={b.id} onClick={() => setSelected(b)}
                      className="text-left rounded-2xl border border-white/10 bg-gradient-to-br from-[#121212] to-[#0a0a0a] p-4 flex items-center gap-4 hover:border-[#C5A059]/40 transition-colors"
                      data-testid={`benefit-card-${b.id}`}>
                {b.logo_b64
                  ? <img src={b.logo_b64} alt={b.partner_name} className="w-14 h-14 rounded-xl object-contain bg-white/5 border border-white/10 shrink-0" />
                  : <div className="w-14 h-14 rounded-xl bg-[#C5A059]/10 border border-[#C5A059]/30 text-[#C5A059] flex items-center justify-center text-xl font-semibold shrink-0">
                      {b.partner_name?.[0]}
                    </div>}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-medium truncate">{b.partner_name}</p>
                  <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{b.details}</p>
                  {(b.website_url || b.phone) && (
                    <div className="flex items-center gap-3 mt-1.5 text-[10px] text-[#C5A059]">
                      {b.website_url && <span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" /> Web</span>}
                      {b.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> Poziv</span>}
                    </div>
                  )}
                </div>
                {b.discount_label && (
                  <span className="shrink-0 text-sm font-bold px-3 py-1.5 rounded-full bg-[#C5A059] text-black"
                        data-testid={`benefit-badge-${b.id}`}>
                    {b.discount_label}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {selected && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
               onClick={() => setSelected(null)} data-testid="benefit-modal">
            <div className="w-full max-w-md rounded-2xl border border-[#C5A059]/30 bg-[#0d0d0d] p-5"
                 onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start gap-4">
                {selected.logo_b64
                  ? <img src={selected.logo_b64} alt={selected.partner_name} className="w-14 h-14 rounded-xl object-contain bg-white/5 border border-white/10" />
                  : <div className="w-14 h-14 rounded-xl bg-[#C5A059]/10 border border-[#C5A059]/30 text-[#C5A059] flex items-center justify-center text-xl font-semibold">
                      {selected.partner_name?.[0]}
                    </div>}
                <div className="flex-1 min-w-0">
                  <p className="text-base text-white font-medium">{selected.partner_name}</p>
                  {selected.discount_label && (
                    <span className="inline-flex items-center gap-1 mt-1 text-xs font-bold px-2.5 py-0.5 rounded-full bg-[#C5A059] text-black">
                      <BadgePercent className="w-3.5 h-3.5" /> {selected.discount_label}
                    </span>
                  )}
                </div>
                <button onClick={() => setSelected(null)} className="p-2 rounded-full text-zinc-400 hover:bg-white/5 transition"
                        data-testid="benefit-modal-close">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-zinc-300 mt-4">{selected.details}</p>
              {selected.instructions && (
                <div className="mt-4 rounded-xl border border-[#C5A059]/20 bg-[#C5A059]/5 p-4">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059] mb-1.5">Kako iskoristiti</p>
                  <p className="text-sm text-zinc-200 whitespace-pre-line" data-testid="benefit-modal-instructions">
                    {selected.instructions}
                  </p>
                </div>
              )}
              {(selected.website_url || selected.phone) && (
                <div className="mt-4 grid grid-cols-2 gap-2">
                  {selected.website_url && (
                    <a href={selected.website_url.startsWith("http") ? selected.website_url : `https://${selected.website_url}`}
                       target="_blank" rel="noopener noreferrer"
                       className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#C5A059] text-black text-sm font-semibold hover:bg-[#D4AF37] transition ${!selected.phone ? "col-span-2" : ""}`}
                       data-testid="benefit-website-link">
                      <Globe className="w-4 h-4" /> Web stranica
                    </a>
                  )}
                  {selected.phone && (
                    <a href={`tel:${selected.phone.replace(/[^+\d]/g, "")}`}
                       className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-[#C5A059]/50 text-[#C5A059] text-sm font-semibold hover:bg-[#C5A059]/10 transition ${!selected.website_url ? "col-span-2" : ""}`}
                       data-testid="benefit-phone-link">
                      <Phone className="w-4 h-4" /> Nazovi
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
